import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { TITLE_OPTIONS, ID_TYPE_OPTIONS, LANGUAGE_OPTIONS, COUNTRY_OPTIONS, CURRENCY_OPTIONS } from '../pages/user/vendorFieldConfig';

// ── Searchable select (shared) ────────────────────────────────────────────────
export const SearchSelect = ({ value, options, onChange, placeholder = 'Search by code or name…', readOnly }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const wrapRef           = useRef(null);

  const selectedLabel = value
    ? (() => { const t = options.find(o => o.code === value); return t ? `${t.code}${t.description ? ` \u2013 ${t.description}` : ''}` : value; })()
    : '';

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return options;
    return options.filter(o => o.code.toUpperCase().includes(q) || (o.description || '').toUpperCase().includes(q));
  }, [query, options]);

  const select = useCallback(code => { onChange(code); setQuery(''); setOpen(false); }, [onChange]);

  if (readOnly) return <div style={SS.valueBox}>{selectedLabel || value || '—'}</div>;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={SS.inputWrap}>
        <input style={SS.input} value={open ? query : selectedLabel}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true); }} />
        {value && <button type="button" style={SS.clear} onClick={() => { onChange(''); setQuery(''); setOpen(false); }}>✕</button>}
        <span style={SS.arrow} onClick={() => setOpen(o => !o)}>▾</span>
      </div>
      {open && (
        <div style={SS.dropdown}>
          {filtered.length === 0
            ? <div style={SS.noMatch}>No match</div>
            : filtered.map(o => (
              <div key={o.code}
                style={{ ...SS.option, ...(o.code === value ? SS.optionActive : {}) }}
                onMouseDown={() => select(o.code)}>
                <span style={SS.optCode}>{o.code}</span>
                {o.description && <span style={SS.optDesc}>{o.description}</span>}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const SS = {
  inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  input: { width: '100%', padding: '9px 56px 9px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, color: '#0B1554', background: '#f8faff', boxSizing: 'border-box', outline: 'none' },
  clear: { position: 'absolute', right: 28, background: 'none', border: 'none', cursor: 'pointer', color: '#8892b0', fontSize: 12, padding: '0 4px' },
  arrow: { position: 'absolute', right: 10, cursor: 'pointer', color: '#8892b0', fontSize: 12, userSelect: 'none' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(11,21,84,0.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4 },
  option: { display: 'flex', alignItems: 'baseline', gap: 8, padding: '9px 13px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f2f8' },
  optionActive: { background: '#f0f4ff' },
  optCode: { fontWeight: 700, color: '#0B1554', minWidth: 48, flexShrink: 0 },
  optDesc: { color: '#4a5568', fontSize: 12.5 },
  noMatch: { padding: '10px 13px', fontSize: 13, color: '#8892b0' },
  valueBox: { padding: '9px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, background: '#f8faff', color: '#0B1554', minHeight: 38 },
};

// ── Main renderer ─────────────────────────────────────────────────────────────
const VendorFieldInput = ({ field: f, value, onChange, options, ifscStatus, readOnly }) => {
  const val = value ?? '';

  if (readOnly) {
    if (f.type === 'yesno') return <div style={SS.valueBox}>{val === 'X' ? 'Yes' : val === '' ? 'No' : '\u2014'}</div>;
    const opts = options || (f.type === 'searchselect' && f.options) || null;
    if (opts && val) {
      const found = opts.find(o => o.code === val);
      const label = found ? `${found.code}${found.description ? ` \u2013 ${found.description}` : ''}` : val;
      return <div style={SS.valueBox}>{label}</div>;
    }
    return <div style={{ ...SS.valueBox, color: val ? '#0B1554' : '#94a3b8' }}>{val || '\u2014'}</div>;
  }

  if (f.type === 'yesno') {
    return (
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', padding: '10px 0' }}>
        {['Yes', 'No'].map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#0B1554', cursor: 'pointer', fontWeight: 600 }}>
            <input type="radio" name={f.key} value={opt}
              checked={opt === 'Yes' ? val === 'X' : val === ''}
              onChange={() => onChange(opt === 'Yes' ? 'X' : '')}
              style={{ width: 16, height: 16, accentColor: '#1A2C80', cursor: 'pointer' }} />
            {opt}
          </label>
        ))}
      </div>
    );
  }

  if (f.type === 'searchselect' && f.options) {
    return <SearchSelect value={val} options={f.options} onChange={onChange} />;
  }

  if (options) {
    return (
      <div>
        <SearchSelect value={val} options={options} onChange={onChange} />
        {f.key === 'bank_key_ifsc' && ifscStatus === 'ok' && (
           <div style={{ fontSize: 11.5, color: '#1A2C80', marginTop: 4, fontWeight: 600 }}>✓ Bank Name auto-filled</div>

        )}
      </div>
    );
  }

  return (
    <input
      style={{ width: '100%', padding: '9px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, color: '#0B1554', background: '#f8faff', boxSizing: 'border-box' }}
      type={f.type || 'text'}
      value={val}
      onChange={e => onChange(e.target.value)}
      placeholder={`Enter ${f.label}`}
    />
  );
};

// ── Helper: resolve SAP options for a vendor field key ────────────────────────
export const resolveVendorOptions = (key, sapOptions) => {
  const { companyCodes, bpGroupings, paymentTerms, purchasingOrgs, incoterms, planningGroups, regions, bankKeys, withholdingTaxTypes, withholdingTaxCodes } = sapOptions;
  switch (key) {
    case 'company_code':              return companyCodes;
    case 'bp_grouping':               return bpGroupings;
    case 'terms_of_payment_key':
    case 'payment_terms':             return paymentTerms;
    case 'purchase_organisation':     return purchasingOrgs;
    case 'incoterms':                 return incoterms;
    case 'planning_group':            return planningGroups;
    case 'region':                    return regions;
    case 'bank_key_ifsc':             return bankKeys;
    case 'withholding_tax_type':      return withholdingTaxTypes;
    case 'withholding_tax_code_type': return withholdingTaxCodes;
    default:                          return null;
  }
};

// ── Helper: resolve SAP options for a material field key ─────────────────────
export const resolveMaterialOptions = (key, matOptions) => {
  const {
    materialTypes, plants, salesOrgs,
    materialGroups, materialGroups1, materialGroups2, materialGroups3, materialGroups4, materialGroups5,
    controlCodes, uoms, mrpTypes, valuationTypes, lotSizes, inspectionTypes,
    storageLocations, profitCenters, distributionChannels, divisions,
  } = matOptions;
  switch (key) {
    case 'material_type':        return materialTypes;
    case 'plant':                return plants;
    case 'sales_organization':   return salesOrgs;
    case 'material_group':       return materialGroups;
    case 'material_group_1':     return materialGroups1;
    case 'material_group_2':     return materialGroups2;
    case 'material_group_3':     return materialGroups3;
    case 'material_group_4':     return materialGroups4;
    case 'material_group_5':     return materialGroups5;
    case 'control_code':         return controlCodes;
    case 'base_uom':
    case 'weight_unit':          return uoms;
    case 'mrp_type':             return mrpTypes;
    case 'valuation_type':       return valuationTypes;
    case 'lot_size':             return lotSizes;
    case 'inspection_type':      return inspectionTypes;
    case 'storage_location':     return storageLocations;
    case 'profit_center':        return profitCenters;
    case 'distribution_channel': return distributionChannels;
    case 'division':             return divisions;
    default:                     return null;
  }
};

// ── Unified resolver: picks vendor or material options by source ──────────────
export const resolveFieldOptions = (key, source, vendorOptions, materialOptions) => {
  if (source === 'vendor_requests')   return resolveVendorOptions(key, vendorOptions);
  if (source === 'material_requests') return resolveMaterialOptions(key, materialOptions);
  return null;
};

export default VendorFieldInput;
